// Shared utility functions for metric calculations

import type { MetricsDataItem, DurationMetricsItem, DispositionMetricsItem } from '@/entities/dailyMetric'

// Type alias for metrics calculation input - accepts the base type
export type MetricItemInput = MetricsDataItem

/**
 * Calculate enhanced metrics for a single item
 */
export function calculateItemMetrics(
  item: MetricItemInput,
  options: { raw?: boolean } = {}
): Record<string, number | string> {
  const cost = parseFloat((item.billing_data?.cost ?? 0).toString())
  const generalMetrics = item.generalMetrics || {}
  const techMetrics = item.techMetrics || {}

  // Calculate key metrics from raw values
  // Total calls = total_dials if present, otherwise general_metrics Total calls
  const totalDials = (item.dialer_metrics?.total_dials ?? generalMetrics['Total calls']) || 0
  const connectedToAI = generalMetrics['Total calls'] || 0
  const connectedCalls = generalMetrics['Connected calls'] || 0 // This is "Connected to customer"
  const successfulCalls = generalMetrics['Successful calls'] || 0
  const totalMinutes = generalMetrics['Total minutes'] || 0

  const rawMetrics: Record<string, number> = {
    // Raw metrics from backend - spread first like calls_platform
    ...generalMetrics,
    ...techMetrics,

    // Simplified key metrics (matching calls_platform's calculated values)
    'Total minutes': totalMinutes,
    'Average handling time': connectedToAI > 0 ? (totalMinutes * 60) / connectedToAI : 0, // seconds per Connected to AI
    'Total calls': totalDials,
    'Connected to AI': connectedToAI,
    'Connected to customer': connectedCalls,
    'Successful calls': successfulCalls,
    '% Connected to AI': totalDials > 0 ? (connectedToAI / totalDials) * 100 : 0, // Connected to AI / Total calls
    '% Connected to customer': connectedToAI > 0 ? (connectedCalls / connectedToAI) * 100 : 0, // Connected to customer / Connected to AI
    '% Successful calls': connectedCalls > 0 ? (successfulCalls / connectedCalls) * 100 : 0,
    'Cost per success': successfulCalls > 0 && cost > 0 ? cost / successfulCalls : 0,
    'Cost ($)': cost,

    // Additional tech metrics
    '% Latency over 2s': totalDials > 0 ? ((techMetrics['Latency over 2s'] || 0) / totalDials) * 100 : 0,
    '% Error': totalDials > 0 ? ((techMetrics['Error'] || 0) / totalDials) * 100 : 0,
  }

  // Return raw numbers if requested (for charts which handle formatting in UI)
  if (options.raw) {
    return rawMetrics
  }

  // Format values for display
  const formattedMetrics: Record<string, string> = {}
  Object.entries(rawMetrics).forEach(([key, value]) => {
    formattedMetrics[key] = formatMetricValue(key, value)
  })

  return formattedMetrics
}

/**
 * Format a single value based on its key and type
 */
function formatMetricValue(key: string, value: number): string {
  if (key.includes('%')) {
    return value.toFixed(2) + '%'
  } else if (key.toLowerCase().includes('cost')) {
    return value.toFixed(2)
  } else if (key === 'Average handling time') {
    return value.toFixed(1)
  } else if (Number.isInteger(value)) {
    return value.toString()
  } else {
    return value.toFixed(1)
  }
}

// Constants
export const UNCONNECTED_DISPOSITIONS = new Set([
  'DF',
  'NA',
  'A',
  'AM',
  'DAIR',
  'TECH',
  'DROP',
  'AA',
  'AB',
])

/**
 * Standard metric order for display
 */
export const COMMON_METRIC_ORDER = [
  'Total minutes',
  'Average handling time',
  'Total calls',
  'Connected to AI',
  'Connected to customer',
  'Successful calls',
  '% Connected to AI',
  '% Connected to customer',
  '% Successful calls',
  'Cost per success',
  'Cost ($)',
]

/**
 * Process disposition metrics for dashboard tables
 */
export function processDispositionMetrics(
  dispositionMetrics: DispositionMetricsItem[],
  metricsData?: MetricsDataItem[]
) {
  // Get all disposition types from raw data
  const allDispositionTypes = dispositionMetrics
    .flatMap((item) => Object.keys(item.rawDispositions || {}))
    .filter((dispo, index, array) => array.indexOf(dispo) === index)
    .filter((dispo): dispo is string => typeof dispo === 'string')

  // Separate connected and unconnected dispositions (with sorting for consistency)
  const connectedTypes = allDispositionTypes.filter((disposition) => !UNCONNECTED_DISPOSITIONS.has(disposition)).sort()
  const unconnectedTypes = allDispositionTypes.filter((disposition) => UNCONNECTED_DISPOSITIONS.has(disposition)).sort()

  const dates = Array.from(new Set(dispositionMetrics.map((item) => item.date))) as string[]

  // Helper to create rows for a disposition type list with calculated percentages
  const createRows = (dispositionTypes: string[], isConnectedGroup: boolean = false) => {
    return dispositionTypes.map((dispositionType, index) => {
      const row: Record<string, string | number> = {
        id: index,
        disposition: dispositionType,
      }
      dispositionMetrics.forEach((item) => {
        const rawDispositions = item.rawDispositions || {}
        const dispositionCount = rawDispositions[dispositionType] || 0

        // Calculate percentage based on whether it's connected or unconnected group
        let percentage = 0
        if (isConnectedGroup) {
          // For connected dispositions, use sum of all connected dispositions as base
          const connectedCalls = Object.entries(rawDispositions)
            .filter(([key]) => !UNCONNECTED_DISPOSITIONS.has(key))
            .reduce((sum: number, [, count]) => sum + (count || 0), 0)
          if (connectedCalls > 0) {
            percentage = (dispositionCount / connectedCalls) * 100
          }
        } else {
          // For unconnected dispositions, use total calls from metricsData as base
          if (metricsData) {
            const dayMetric = metricsData.find((m) => m.date === item.date)
            const totalCalls =
              dayMetric?.dialer_metrics?.total_dials || dayMetric?.generalMetrics?.['Total calls'] || 0
            if (totalCalls > 0) {
              percentage = (dispositionCount / totalCalls) * 100
            }
          } else {
            // Fallback to total dispositions if metricsData not provided
            const totalCalls = Object.values(rawDispositions).reduce((sum: number, count) => sum + (count || 0), 0)
            if (totalCalls > 0) {
              percentage = (dispositionCount / totalCalls) * 100
            }
          }
        }

        row[item.date] = percentage > 0 ? percentage.toFixed(2) : ''
      })
      return row
    })
  }

  return {
    connectedTypes,
    unconnectedTypes,
    dates,
    createRows,
  }
}

/**
 * Process duration metrics for dashboard tables
 */
export function processDurationMetrics(durationMetrics: DurationMetricsItem[]) {
  // Get metric types, filter for percentage ones only, and sort them
  const metricTypes = durationMetrics
    .flatMap((item) => Object.keys(item.metrics || {}))
    .filter((metric, index, array) => array.indexOf(metric) === index)
    .filter((metric): metric is string => typeof metric === 'string' && metric.includes('%'))

  const sortedMetricTypes = sortMetricNames(metricTypes) as string[]

  const rows = sortedMetricTypes.map((metricType: string, index: number) => {
    const row: Record<string, string | number> = {
      id: index,
      metric: metricType,
    }
    durationMetrics.forEach((item) => {
      row[item.date] = (item.metrics[metricType] || 0).toFixed(2)
    })
    return row
  })

  const dates = Array.from(new Set(durationMetrics.map((metric) => metric.date)))

  return {
    rows,
    dates,
  }
}

/**
 * Sort metric names - handles numeric ranges in metric names (e.g., "% 0-15s", "% 15-30s")
 */
export function sortMetricNames(names: string[]): string[] {
  return [...names].sort((a, b) => {
    // Extract numeric values from strings like "% 0-15s", "% 15-30s", etc.
    const getNumericValue = (str: string) => {
      const match = str.match(/(\d+)/)
      return match ? parseInt(match[1], 10) : Infinity
    }
    return getNumericValue(a) - getNumericValue(b)
  })
}

/**
 * Get unique values from an array
 */
export function uniqueValues<T>(array: T[]): T[] {
  return [...new Set(array)]
}

/**
 * Sort dates in ascending or descending order
 */
export function sortDates(dates: string[], order: 'asc' | 'desc' = 'desc'): string[] {
  return [...dates].sort((a, b) => {
    const dateA = new Date(a).getTime()
    const dateB = new Date(b).getTime()
    return order === 'asc' ? dateA - dateB : dateB - dateA
  })
}

/**
 * Format date string for display
 */
export function formatDateDisplay(dateStr: string, format: 'short' | 'full' = 'short'): string {
  const date = new Date(dateStr + 'T00:00:00Z')
  if (format === 'full') {
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' })
}